import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router'
import queryString from 'qs'
import debounce from 'lodash/debounce'
import get from 'lodash/get'

import normalizeKeys from 'components/utils/propsUtils'

import { fetchEnquiryPrices } from '../../../actions/enquiryActions'

import Button from '../../../components/display/Button'
import Overlay from '../../../components/display/Overlay'
import InstantBookBadge from '../../../components/display/InstantBookBadge'
import PricingBreakdown from '../../../components/pricing/PricingBreakdown'
import { AuthToken } from '../../../components/auth/shared'
import { formatPrice } from '../../../components/utils/pricing'
import { slugToNice } from '../../../components/utils/stringUtils'

import BookingFormFields from './BookingFormFields'
import { unbookableWithReason } from './helpers'

export const BookingFormWrapper = styled.div`
  position: relative;
  padding: 32px 24px;
  background-color: ${({ theme }) => theme.brand.sand};
  border-radius: 6px;

  .DateRangePicker {
    width: 100%;
    margin-bottom: 8px;
  }

  .SingleDatePicker {
    margin-bottom: 16px;
  }

  .DateRangePickerInput,
  .SingleDatePickerInput {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 0;
    border-color: #d5d5d5;
  }

  ${Button} {
    width: 100%;
    margin-top: 24px;
  }
`
const BookingErrors = styled.div`
  margin-bottom: 10px;
  text-align: center;
  color: ${({ theme }) => theme.colors.errorRed};
`

const Header = styled.header`
  margin-bottom: 24px;
  color: ${({ theme }) => theme.brand.forest};

  font-size: 50px;

  strong {
    font-weight: 500;
    margin-right: 8px;
  }

  small {
    font-size: 0.4em;
  }
`

const Footnote = styled.footer`
  width: 100%;
  margin-top: 16px;
  color: ${({ theme, warning }) =>
    warning
      ? theme?.['-ds-warning-300']?.value
      : theme?.['-ds-neutral-600']?.value};
  font-size: 14px;
  font-weight: 300;
  text-align: center;
`

export const validDate = (value) =>
  !value ? 'Please add dates to request a booking' : undefined

const BookingForm = ({
  location,
  listingId,
  listingBookable,
  doNotShowPayoutTotal,
  storyId,
}) => {
  const dispatch = useDispatch()
  const listing = useSelector((state) => state.listing.data)
  const enquiry = useSelector((state) => state.enquiry)
  const formRef = useRef(null)
  const [submitting, setSubmitting] = useState(false)
  // Block the form via Overlay until JS takes over; must render on both SSR
  // and first client render to keep hydration matched.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])

  const initialBooking = useMemo(
    // Action-cache for /listings/:id ignores the query string, so the SSR
    // HTML is rendered without any query-derived values. Reading
    // location.search here would diverge from SSR on URLs like
    // ?story_id=1661 and trip React 18 hydration recovery (minified React
    // errors 418/423 — https://react.dev/errors/418).
    // The query-sync effect below applies overrides after mount.
    // initialBooking is computed once per mount; re-rendering won't
    // re-initialize the form (matches enableReinitialize+keepDirtyOnReinitialize)
    () => ({
      adults: enquiry?.data?.adults || (listing?.capacity === 1 ? 1 : 2),
      children: enquiry?.data?.children || 0,
      pets: enquiry?.data?.pets || 0,
      start_date: get(enquiry?.data, 'start_date', null),
      end_date: get(enquiry?.data, 'end_date', null),
      story_id: storyId,
    }),
    [], // eslint-disable-line
  )

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { booking: initialBooking },
  })
  const { handleSubmit, control } = methods
  const formValues = useWatch({ control })

  useEffect(() => {
    const { adults, children, pets, startDate, endDate, ...queryParams } =
      queryString.parse(location.search, { ignoreQueryPrefix: true })

    const overrides = {}
    // parseInt('abc', 10) is NaN — falsy — so a non-numeric ?adults=,
    // ?children= or ?pets= correctly falls through to the SSR defaults
    // instead of resetting the form to NaN.
    const parsedAdults = parseInt(adults, 10)
    if (parsedAdults) overrides.adults = parsedAdults
    const parsedChildren = parseInt(children, 10)
    if (parsedChildren) overrides.children = parsedChildren
    const parsedPets = parseInt(pets, 10)
    if (parsedPets) overrides.pets = parsedPets
    if (startDate) overrides.start_date = startDate
    if (endDate || startDate) overrides.end_date = endDate || startDate
    if (queryParams.story_id) overrides.story_id = queryParams.story_id

    if (Object.keys(overrides).length === 0) return
    methods.reset({ booking: { ...initialBooking, ...overrides } })
    // Runs once on mount; mirrors initialBooking's empty deps.
  }, []) // eslint-disable-line

  const debouncedFetchPrices = useMemo(
    () =>
      debounce((booking) => {
        dispatch(fetchEnquiryPrices(listingId, booking))
      }, 250),
    [dispatch, listingId],
  )

  useEffect(() => () => debouncedFetchPrices.cancel(), [debouncedFetchPrices])

  useEffect(() => {
    const booking = formValues?.booking
    if (booking?.start_date && booking?.end_date) {
      debouncedFetchPrices(booking)
    }
  }, [
    formValues?.booking?.start_date,
    formValues?.booking?.end_date,
    formValues?.booking?.adults,
    formValues?.booking?.children,
    formValues?.booking?.pets,
    debouncedFetchPrices,
  ])

  useEffect(() => {
    // Skip this initial dispatch if the URL carries date params — the
    // query-sync effect above will methods.reset and the watcher below will
    // then fire a correctly-dated debouncedFetchPrices. Without this guard
    // the no-args dispatch races with the dated one and a slow-resolving
    // undefined response can clobber the enquiry state in Redux.
    const querySearch = queryString.parse(location.search, {
      ignoreQueryPrefix: true,
    })
    if (querySearch.startDate || querySearch.endDate) return

    const {
      adults: a,
      children: c,
      pets: p,
      start_date: s,
      end_date: e,
    } = initialBooking
    if (s) {
      dispatch(
        fetchEnquiryPrices(listingId, {
          adults: a,
          children: c,
          pets: p,
          start_date: s,
          end_date: e,
        }),
      )
    } else {
      dispatch(fetchEnquiryPrices(listingId, undefined))
    }
  }, [dispatch, listingId, initialBooking, location.search])

  const hasErrors = enquiry.data && enquiry.data.errors.length

  const renderBookingErrors = () => {
    if (hasErrors) {
      return <BookingErrors>{enquiry.data.errors[0]}</BookingErrors>
    }
    return null
  }

  const hasTotal = enquiry.data && enquiry.data.total > 0
  const enquiryListing = enquiry.data && enquiry.data.listing

  const disabledReason = unbookableWithReason(listingBookable)
  const footNote = listing.instant_book_enabled
    ? 'No payment required yet'
    : 'No payment required until your host accepts'

  let canRequestBooking = false
  if (formValues && enquiryListing) {
    canRequestBooking = listing.status === 'published'
  }
  if (disabledReason) {
    canRequestBooking = false
  }

  const onSubmit = () => {
    setSubmitting(true)
    if (formRef.current) formRef.current.submit()
  }

  return (
    <BookingFormWrapper className="qa-booking-form">
      {hydrated && (
        <FormProvider {...methods}>
          <form
            ref={formRef}
            action={`${listing.url}/bookings`}
            method="post"
            onSubmit={handleSubmit(onSubmit)}
          >
            <AuthToken />

            <Header>
              <strong>
                {hasTotal
                  ? formatPrice(enquiry.data.total, 0)
                  : formatPrice(listing.price, 0)}
              </strong>
              <small>
                {(() => {
                  if (hasTotal) {
                    return 'total'
                  }
                  return slugToNice(listing.pricing_suffix)
                })()}
              </small>
            </Header>

            {listing.instant_book_enabled && (
              <div style={{ position: 'relative', top: -18 }}>
                <InstantBookBadge />
              </div>
            )}

            {renderBookingErrors()}

            <BookingFormFields
              listingId={listingId}
              listing={listing}
              enquiry={enquiry}
              formValues={formValues}
            />

            <input
              id="booking_story_id"
              type="hidden"
              {...methods.register('booking[story_id]')}
            />

            {hasTotal && enquiry.data && (
              <PricingBreakdown
                pricing={normalizeKeys(enquiry.data)}
                doNotShowPayoutTotal={doNotShowPayoutTotal}
              />
            )}

            <Button
              type="submit"
              disabled={!canRequestBooking}
              uppercase={false}
            >
              {listing.instant_book_enabled ? 'Book now' : 'Request to book'}
            </Button>

            <Footnote warning={disabledReason != null}>
              {disabledReason || footNote}
            </Footnote>
          </form>
        </FormProvider>
      )}

      {(!hydrated || submitting) && <Overlay />}
    </BookingFormWrapper>
  )
}

export default withRouter(BookingForm)
